import { nanoid } from "nanoid"
import { jobShopTag, tenantTag } from "@/utils/cacheTags"
import { paletteByKey, resolveColorSlots } from "@/utils/colorSlots"
import Logger from "@/utils/Logger"

interface JobShopDataRequest {
  $api: ReturnType<typeof useNuxtApp>["$api"]
  tenantId: string
  jobShopId: string
  acceptLanguage: string
  dataCacheTtl: number
  dataCacheStaleTtl: number
  requestId: string
}

/**
 * The JobShop's settings from the shared data cache, colour-resolved. The one
 * place the "jobShopData" cache entry is read, so useJobShopData and
 * usePasswordGateBranding (JET-2155) share it rather than fetch twice. A plain
 * function: callers capture `$api` and the config before any await.
 */
export async function fetchResolvedJobShopData(request: JobShopDataRequest): Promise<JobShop> {
  const { $api, tenantId, jobShopId, acceptLanguage, dataCacheTtl, dataCacheStaleTtl, requestId } = request

  // Stale-if-error (JET-2212): serve fresh within dataCacheTtl, then keep serving
  // last-known-good for dataCacheStaleTtl if a refetch fails (unless it's a 4xx).
  const data = await useDataCacheWithStale("jobShopData", () => $api.jobShop.getJobShopData(tenantId, jobShopId, acceptLanguage), {
    maxAge: dataCacheTtl,
    staleIfError: dataCacheStaleTtl,
    requestId,
    tags: [tenantTag(tenantId), jobShopTag(jobShopId)],
  })

  // Resolved here rather than on a returned ref, so the Nuxt payload carries the
  // resolved settings and every useNuxtData("jobShopData") reader sees them too — the
  // survey summary page reads the palette-backed search colours that way. The shared
  // data cache above still holds the unresolved payload.
  return resolveColorSlots(data, paletteByKey(data))
}

export async function useJobShopData() {
  const { $api } = useNuxtApp()
  const { t } = useI18n()
  const { tenantId, jobShopId, acceptLanguage } = useHeaderStore()
  const { dataCacheTtl, dataCacheStaleTtl } = useRuntimeConfig()

  const requestId = nanoid()
  const contextString = `JobShop data for tenantId: "${tenantId}" and jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const {
    data: jobShop,
    status: jobShopStatus,
    error: jobShopError,
  } = await useAsyncData("jobShopData", () =>
    fetchResolvedJobShopData({ $api, tenantId, jobShopId, acceptLanguage, dataCacheTtl, dataCacheStaleTtl, requestId }))

  if (import.meta.server) {
    if (!jobShopError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = jobShopError.value?.data?.errors?.[0]?.message || jobShopError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, jobShopError.value)
    }
  }

  if (jobShopError.value) {
    throw createError({
      statusCode: jobShopError.value.statusCode,
      statusMessage: t("error-messages.general"),
    })
  }

  return { jobShop, jobShopStatus, jobShopError }
}
